import {
    CardPointeBrowserTokenizationSessionType,
    CardPointeTokenizationResultType,
    resolveCardPointeHostedIframeToken,
    tokenizeCardWithCardPointeCardSecure,
    CloverBrowserTokenizationSessionType,
    CloverTokenizationResultType,
    JsonObjectSchema,
    type GatewayCardTokenizationResponse,
    type GetDelegateTokenResponse,
} from '@pillar-portal/api-types';
import { describeProviderHttpFailure } from '@pillar-portal/api-types';
import { parseJsonWithNativeSafeNumbers } from '@pillar-portal/api-types';
import {
    ValorBrowserTokenizationSessionSchema,
    ValorBrowserTokenizationResultV2Schema,
    parseValorCardTokenizationFunctionalResponse,
} from '@pillar-portal/api-types/schemas/ipg/valor/payment';
import {
    ValorTokenizationResultType,
    type ValorCardTokenizationResponse,
} from '@pillar-portal/api-types/ipg/valor/payment';
import type { CardFormData } from '../card-payment-entry';
import { buildValorCardTokenizationRequest } from './valor/card-tokenization';
import { tokenizeCardWithCloverEcommerce } from './clover/card-tokenization';

export type GatewayDelegateTokenizationFetch = (
    input: RequestInfo | URL,
    init?: RequestInit
) => Promise<Response>;

export interface GatewayDelegateTokenizationInput {
    readonly cardData: CardFormData;
    readonly delegateToken: GetDelegateTokenResponse;
    readonly fetchImplementation?: GatewayDelegateTokenizationFetch;
}

export type GatewayDelegateTokenizationResponse = GatewayCardTokenizationResponse;

export async function tokenizeCardWithGatewayDelegateToken(
    input: GatewayDelegateTokenizationInput
): Promise<GatewayDelegateTokenizationResponse> {
    const sessionType = input.delegateToken.browser_tokenization_session_type;
    switch (sessionType) {
        case undefined:
            return tokenizeCardWithValorDelegateToken(input);
        case CardPointeBrowserTokenizationSessionType:
            return await tokenizeCardWithCardPointeCardSecure({
                cardData: input.cardData,
                cardSecureBaseUrl: input.delegateToken.card_secure_base_url,
                fetchImplementation: input.fetchImplementation,
            });
        case CloverBrowserTokenizationSessionType:
            return await tokenizeCardWithCloverEcommerce({
                cardData: input.cardData,
                ecommerceApiKey: input.delegateToken.ecommerce_api_key,
                ecommerceTokenizationUrl: input.delegateToken.ecommerce_tokenization_url,
                fetchImplementation: input.fetchImplementation,
            });
        default:
            throw new Error('Unsupported browser tokenization session type.');
    }
}

export function resolveGatewayTokenizationPaymentToken(
    response: GatewayDelegateTokenizationResponse
): string {
    if ('cardToken' in response) {
        return response.cardToken;
    }

    switch (response.result_type) {
        case ValorTokenizationResultType.BROWSER_TOKENIZATION_V2:
            return response.token;
        case CardPointeTokenizationResultType.CARDSECURE_BROWSER_TOKENIZATION:
            return response.cardsecure_response.token;
        case CardPointeTokenizationResultType.CARDSECURE_BROWSER_TOKENIZATION_V2:
            return response.token;
        case CardPointeTokenizationResultType.CARDSECURE_HOSTED_IFRAME_TOKENIZATION:
            return resolveCardPointeHostedIframeToken(response.cardsecure_response);
        case CardPointeTokenizationResultType.CARDSECURE_HOSTED_IFRAME_TOKENIZATION_V2:
            return response.token;
        case CardPointeTokenizationResultType.STORED_CARD_METADATA:
            return response.token;
        case CloverTokenizationResultType.ECOMMERCE_BROWSER_TOKENIZATION:
            return response.token_response.token;
        case CloverTokenizationResultType.ECOMMERCE_BROWSER_TOKENIZATION_V2:
            return response.token;
        case CloverTokenizationResultType.STORED_CARD_METADATA:
            return response.token;
    }
}

export function resolveGatewayTokenizationCardBrand(input: {
    readonly response: GatewayDelegateTokenizationResponse;
    readonly fallbackCardBrand: string;
}): string {
    if ('card_brand' in input.response) {
        return input.response.card_brand;
    }

    if ('card_metadata' in input.response) {
        return input.response.card_metadata?.card_brand ?? input.fallbackCardBrand;
    }

    return input.fallbackCardBrand;
}

export function resolveGatewayTokenizationPanPrefix(
    response: GatewayDelegateTokenizationResponse
): string | undefined {
    if ('cardToken' in response) {
        return undefined;
    }

    switch (response.result_type) {
        case ValorTokenizationResultType.BROWSER_TOKENIZATION_V2:
        case CardPointeTokenizationResultType.CARDSECURE_BROWSER_TOKENIZATION:
        case CardPointeTokenizationResultType.CARDSECURE_BROWSER_TOKENIZATION_V2:
        case CardPointeTokenizationResultType.CARDSECURE_HOSTED_IFRAME_TOKENIZATION:
        case CardPointeTokenizationResultType.CARDSECURE_HOSTED_IFRAME_TOKENIZATION_V2:
        case CardPointeTokenizationResultType.STORED_CARD_METADATA:
        case CloverTokenizationResultType.STORED_CARD_METADATA:
            return undefined;
        case CloverTokenizationResultType.ECOMMERCE_BROWSER_TOKENIZATION:
            return response.token_response.card?.first6;
        case CloverTokenizationResultType.ECOMMERCE_BROWSER_TOKENIZATION_V2:
            return response.provider_response.card?.first6;
    }
}

async function tokenizeCardWithValorDelegateToken(
    input: GatewayDelegateTokenizationInput
): Promise<GatewayDelegateTokenizationResponse> {
    const delegateToken = ValorBrowserTokenizationSessionSchema.parse(input.delegateToken);
    const normalizedPan = input.cardData.cardNumber.replace(/\D/g, '');
    const normalizedExpiryDate = input.cardData.expiryDate.replace('/', '');
    const providerRequest = buildValorCardTokenizationRequest({
        delegateToken,
        normalizedPan,
        normalizedExpiryDate,
    });
    const queryParams = new URLSearchParams(providerRequest);
    const fetchImplementation = input.fetchImplementation ?? fetch;

    const response = await fetchImplementation(
        `${delegateToken.domain}/?${queryParams.toString()}`,
        {
            method: 'POST',
            headers: {
                accept: 'application/json',
            },
        }
    );

    const responseBody: unknown = await parseJsonResponse(response);
    if (!response.ok) {
        throw new Error(
            describeProviderHttpFailure({
                provider: 'Valor',
                httpStatus: response.status,
                subject: 'these card details',
            })
        );
    }

    const providerResponseResult = JsonObjectSchema.safeParse(responseBody);
    if (!providerResponseResult.success) {
        throw new Error('Card tokenization response is missing expected fields.');
    }
    let tokenizationResponse: ValorCardTokenizationResponse;
    try {
        tokenizationResponse = parseValorCardTokenizationFunctionalResponse(
            providerResponseResult.data
        );
    } catch {
        throw new Error('Card tokenization response is missing expected fields.');
    }

    return ValorBrowserTokenizationResultV2Schema.parse({
        result_type: ValorTokenizationResultType.BROWSER_TOKENIZATION_V2,
        provider_request: providerRequest,
        provider_response: providerResponseResult.data,
        token: tokenizationResponse.cardToken,
        card_brand: tokenizationResponse.card_brand,
        ...(tokenizationResponse.card_type === undefined
            ? {}
            : { card_type: tokenizationResponse.card_type }),
    });
}

async function parseJsonResponse(response: Response): Promise<unknown> {
    try {
        return parseJsonWithNativeSafeNumbers(await response.text());
    } catch {
        throw new Error('Card tokenization response was not valid JSON.');
    }
}
